import {WixImageInfo} from '../wix-image/wix-image';
import {
    arrayFirst,
    assertDefined,
    evaluateFunction,
    IIsomorphicHttpClient,
    iterableFindAndSelect,
    objectRemoveEntriesWithUndefinedValues,
    stringExtractSubstringUntilMatch,
    Values
} from '@wix/devzai-utils-common';
import {WixSiteFile, WixVideoInfo, wixVideoInfoGetVideoId, wixVideoResolveHlsUrl} from "..";

export type WixMediaManagerContext = {
    siteMediaToken: string;
    metaSiteId: string;
}

export const WixMediaManagerMediaStatus = {
    Ready: 'READY',
    InProgress: 'INPROGRESS'
} as const;

export type WixMediaManagerMediaStatus = Values<typeof WixMediaManagerMediaStatus>;

export namespace WixMediaManager {
    export interface ImageDTO {
        parentFolderId: string;
        hash: string;
        originalFileName: string;
        fileName: string;
        fileUrl: string;
        baseUrl: string;
        fileSize: number;
        iconUrl: string;
        mediaType: string;
        mimeType: string;
        labels: string[];
        tags: string[];
        status: string;
        dateCreated: number;
        dateModified: number;
        userId: string;
        siteId: string;
        height: number;
        width: number;
    }

    export interface SiteFileDto {
        parentFolderId: string;
        hash: string;
        originalFileName: string;
        fileName: string;
        fileUrl: string;
        baseUrl: string;
        fileSize: number;
        iconUrl: string;
        mediaType: typeof WixMediaManagerMediaType.Document;
        mimeType: string;
        labels: string[];
        tags: string[];
        status: string;
        dateCreated: number;
        dateModified: number;
        userId: string;
        siteId: string;
    }

    export interface VideoDTO {
        parentFolderId: string;
        hash: string;
        originalFileName: string;
        fileName: string;
        fileUrl: string;
        baseUrl: string;
        tags: string[];
        outputFiles: {
            videos: {
                quality: string;
            }[];
        };
    }

    type UploadedFileInfoBase<MEDIA extends WixMediaManagerMediaType> = {
        file_name: string;
        file_url: string;
        media_type: MEDIA;
        mime_type: string;
        file_size: number;
        original_file_name: string;
        hash: string;
        tags: string[];
        refs: string[];
        created_ts: number;
        modified_ts: number;
        parent_folder_id: string;
        store: string;
        icon_url: string;
        op_status: string;
        site_id: string;
        user_id: string;
        app_id: string;
    }

    export type UploadedFileInfo =
        UploadedFileInfoBase<typeof WixMediaManagerMediaType.Image | typeof WixMediaManagerMediaType.VectorImage> & {
            height: number;
            width: number;
        } |
        UploadedFileInfoBase<typeof WixMediaManagerMediaType.Video> & {
            file_output: {
                video: ReadonlyArray<{
                    quality: string;
                    url: string;
                }>
            }
        }
}

export interface WixMediaManagerItemBase<MEDIA_TYPE extends WixMediaManagerMediaType> {
    id: string;
    title: string;
    mediaType: MEDIA_TYPE;
    fileSize: number;
    fileName: string;
    uri: string;
    fileUrl: string;
    height: number;
    width: number;
    tags: string[];
    labels: string[];
}

export const WixMediaManagerVideoItemTranscodingStatus = {
    Ready: 'READY',
    InQueue: 'IN-QUEUE',
} as const;

export type WixMediaManagerVideoItemTranscodingStatus = Values<typeof WixMediaManagerVideoItemTranscodingStatus>;

export type WixMediaManagerImageItem = WixMediaManagerItemBase<typeof WixMediaManagerMediaType.Image | typeof WixMediaManagerMediaType.VectorImage>;
export type WixMediaManagerDocumentItem = WixMediaManagerItemBase<typeof WixMediaManagerMediaType.Document>;
export type WixMediaManagerVideoItem = WixMediaManagerItemBase<typeof WixMediaManagerMediaType.Video> & {
    // TODO: Check if this is actually an optionally field or does it always there.
    fileBaseUrl?: string;
    transcodingStatus?: WixMediaManagerVideoItemTranscodingStatus;
    fileOutput: {
        video: {
            quality: string;
            url: string;
        }[];
        image: {
            url: string;
            width: number;
            height: number;
        }[];
        adaptive_video?: {
            format: string;
            url: string;
        }[];
    }
}

export type WixMediaGalleryItem = WixMediaManagerVideoItem | WixMediaManagerImageItem | WixMediaManagerDocumentItem;

export type WixMediaManagerMedia = WixImageInfo | WixVideoInfo | WixSiteFile;

type WixMediaManagerImageOrVideoFileInfo = {
    tags: string[];
    file_name: string;
    file_size: number;
    media_type: string; // `picture` / `video` ?
    original_file_name: string;
    title: string;
    op_status: WixMediaManagerMediaStatus;
}

export type WixMediaManagerImageFileInfo = WixMediaManagerImageOrVideoFileInfo & {
    height: number;
    width: number;
}

export type WixMediaManagerVideoFileInfo = WixMediaManagerImageOrVideoFileInfo & {
    file_input: {
        fps: string;
        video_bitrate: number;
        audio_bitrate: number;
        type: string;
        height: number;
        duration: number;
        width: number;
        display_aspect_ratio: string;
        rotation: number;
        sample_aspect_ratio: string;
    };
    file_output: {
        video: {
            key: string;
            secure: boolean;
            status: WixMediaManagerMediaStatus;
            tag: string;
            url: string;
            video_bitrate: number;
            display_aspect_ratio: string;
            audio_bitrate: number;
            quality: string;
            duration: number;
            format: string;
            width: number;
            fps: string;
            height: number;
        }[];
        storyboard?: {
            key: string;
            secure: boolean;
            status: WixMediaManagerMediaStatus;
            tag: string;
            url: string;
            video_bitrate: number;
            display_aspect_ratio: string;
            audio_bitrate: number;
            quality: string;
            duration: number;
            format: string;
            width: number;
            kind: string;
            fps: string;
            height: number;
        }[];
        image?: {
            key: string;
            secure: boolean;
            status: WixMediaManagerMediaStatus;
            url: string;
            format: string;
            width: number;
            height: number;
        }[];
        adaptive_video?: {
            format: string;
            status: WixMediaManagerMediaStatus;
            tag: string;
            key: string;
            url: string;
        }[];
    }
}

export namespace WixMediaManagerMedia {

    export type ImageOrVideo = Exclude<WixMediaManagerMedia, WixSiteFile>;

    export type ResolveFormMediaManagerItem<T extends WixMediaGalleryItem> =
        T extends WixMediaManagerVideoItem ? WixVideoInfo :
        T extends WixMediaManagerImageItem ? WixImageInfo :
        T extends WixMediaManagerDocumentItem ? WixSiteFile :
        never;

    export type MediaFileInfo = WixMediaManagerImageFileInfo | WixMediaManagerVideoFileInfo;

    export type ResolveFileInfo<T extends ImageOrVideo> =
        T extends WixImageInfo ? WixMediaManagerImageFileInfo :
        T extends WixVideoInfo ? WixMediaManagerVideoFileInfo :
            never;
}

export function wixMediaManagerMediaGetFileInfoUrl (mediaManagerMedia: WixMediaManagerMedia.ImageOrVideo) {

    const fileId = evaluateFunction(() => {
        if (wixMediaManagerMediaIsWixVideoInfo(mediaManagerMedia)) {
            return wixVideoInfoGetVideoId(mediaManagerMedia);
        } else if (wixMediaManagerMediaIsWixImageInfo(mediaManagerMedia)) {
            return mediaManagerMedia.url;
        } else {
            return undefined;
        }
    })

    if (fileId) {
        return `https://files.wix.com/site/media/files/${fileId}/info`;
    }

    return undefined;

}

export async function wixMediaManagerMediaGetFileInfo<M extends WixMediaManagerMedia.ImageOrVideo> (
    httpClient: IIsomorphicHttpClient,
    mediaManagerMedia: M
) : Promise<WixMediaManagerMedia.ResolveFileInfo<M>> {

    const fileInfoUrl = wixMediaManagerMediaGetFileInfoUrl(mediaManagerMedia);

    if (fileInfoUrl) {
        return await httpClient.request<WixMediaManagerMedia.ResolveFileInfo<M>>({
            url: fileInfoUrl,
            method: 'GET',
        })
    }

    throw new Error('Failed to fetch file info');
}

export function wixMediaManagerMediaFileInfoIsVideoFileInfo (mediaFileInfo: WixMediaManagerMedia.MediaFileInfo) : mediaFileInfo is WixMediaManagerVideoFileInfo {
    return (mediaFileInfo as WixMediaManagerVideoFileInfo).file_output !== undefined;
}

export function wixMediaManagerVideoFileInfoGetHlsUrl (videoFileInfo: WixMediaManagerVideoFileInfo) {
    return iterableFindAndSelect(videoFileInfo.file_output?.adaptive_video ?? [], (adaptiveVideoInfo, skip) => {
        return adaptiveVideoInfo.format === 'hls' ?
            wixVideoResolveHlsUrl(adaptiveVideoInfo.url) :
            skip;
    })
}

export function wixMediaManagerMediaIsWixSiteFile (mediaManagerMedia: WixMediaManagerMedia) : mediaManagerMedia is WixSiteFile {
    return (mediaManagerMedia as WixSiteFile).fileUrl !== undefined;
}

export function wixMediaManagerMediaIsWixImageInfo (mediaManagerMedia: WixMediaManagerMedia) : mediaManagerMedia is WixImageInfo {
    return !wixMediaManagerMediaIsWixSiteFile(mediaManagerMedia) && !wixMediaManagerMediaIsWixVideoInfo(mediaManagerMedia);
}

export function wixMediaManagerMediaIsWixVideoInfo (mediaManagerMedia: WixMediaManagerMedia) : mediaManagerMedia is WixVideoInfo {
    return (mediaManagerMedia as WixVideoInfo).quality !== undefined;
}

export function wixMediaManagerItemToWixImageInfo (mediaManagerItem: WixMediaGalleryItem) : WixImageInfo {

    if (mediaManagerItem.mediaType === WixMediaManagerMediaType.Video) {
        const poster = mediaManagerItem.fileOutput.image[0];

        if (poster) {
            return {
                url: poster.url,
                title: mediaManagerItem.title,
                width: poster.width,
                height: poster.height,
                isVectorImage: false
            }
        }
    }

    return objectRemoveEntriesWithUndefinedValues({
        url: mediaManagerItem.uri,
        title: mediaManagerItem.title,
        width: mediaManagerItem.width,
        height: mediaManagerItem.height,
        isVectorImage: mediaManagerItem.mediaType === WixMediaManagerMediaType.VectorImage,
        basePath: (
            mediaManagerItem.mediaType === WixMediaManagerMediaType.VectorImage &&
            mediaManagerItem.fileUrl.startsWith('media/')
        ) ? 'media' : undefined
    })
}

export function wixMediaGalleryItemToWixMediaMediaManagerMedia<T extends WixMediaGalleryItem> (
    mediaManagerItem: T,
    options: {
        metaSiteId: string;
    }
) : WixMediaManagerMedia.ResolveFormMediaManagerItem<T> {

    const {
        metaSiteId
    } = options;

    if (mediaManagerItem.mediaType === WixMediaManagerMediaType.Video) {

        const poster = mediaManagerItem.fileOutput.image[0];

        const videoFilesOutput = mediaManagerItem.fileOutput.video;
        const arbitraryVideoOutput = assertDefined(arrayFirst(videoFilesOutput));

        return objectRemoveEntriesWithUndefinedValues({
            baseUrl: stringExtractSubstringUntilMatch(arbitraryVideoOutput.url, `/${arbitraryVideoOutput.quality}`),
            quality: videoFilesOutput.map(videoFileInfo => videoFileInfo.quality),
            poster: poster ? {
                url: poster.url,
                title: mediaManagerItem.title,
                width: poster.width,
                height: poster.height,
                isVectorImage: false
            } : undefined,
            hlsVideoPath: iterableFindAndSelect(mediaManagerItem.fileOutput.adaptive_video ?? [], (adaptiveVideoInfo, skip) => {
                return adaptiveVideoInfo.format === 'hls' ?
                    adaptiveVideoInfo.url :
                    skip;
            }),
            maybeNotReady: mediaManagerItem.transcodingStatus !== WixMediaManagerVideoItemTranscodingStatus.Ready ? true : undefined
        } satisfies WixVideoInfo) as WixMediaManagerMedia.ResolveFormMediaManagerItem<T>
    } else if (mediaManagerItem.mediaType === WixMediaManagerMediaType.Document) {
        return {
            fileUrl: mediaManagerItem.fileUrl,
            fileSize: mediaManagerItem.fileSize,
            metaSiteId: metaSiteId
        } satisfies WixSiteFile as WixMediaManagerMedia.ResolveFormMediaManagerItem<T>
    } else {
        return wixMediaManagerItemToWixImageInfo(mediaManagerItem) as WixMediaManagerMedia.ResolveFormMediaManagerItem<T>;
    }
}

export function wixMediaManagerUploadedFileInfoToWixMediaMediaManagerMedia (
    uploadFileInfo: WixMediaManager.UploadedFileInfo
) : WixMediaManagerMedia.ImageOrVideo {

    if (uploadFileInfo.media_type === 'video') {

        const videoFilesOutput = uploadFileInfo.file_output.video;

        const arbitraryVideoOutput = assertDefined(arrayFirst(videoFilesOutput));

        return {
            baseUrl: stringExtractSubstringUntilMatch(arbitraryVideoOutput.url, `/${arbitraryVideoOutput.quality}`),
            quality: videoFilesOutput.map(videoFileInfo => videoFileInfo.quality)
        } satisfies WixVideoInfo;
    } else {

        return objectRemoveEntriesWithUndefinedValues({
            url: uploadFileInfo.file_name,
            width: uploadFileInfo.width,
            height: uploadFileInfo.height,
            isVectorImage: uploadFileInfo.media_type === WixMediaManagerMediaType.VectorImage,
            basePath: (
                uploadFileInfo.media_type === WixMediaManagerMediaType.VectorImage &&
                uploadFileInfo.file_url.startsWith('media/')
            ) ? 'media' : undefined
        })
    }
}

export function wixMediaManagerImageDtoToWixImageInfo (imageDto: WixMediaManager.ImageDTO) : WixImageInfo {
    return {
        url: imageDto.fileName,
        width: imageDto.width,
        height: imageDto.height,
        isVectorImage: imageDto.mediaType === WixMediaManagerMediaType.VectorImage
    }
}

export function wixMediaManagerVideoDtoToWixVideoInfo (videoDto: WixMediaManager.VideoDTO) : WixVideoInfo {
    return {
        baseUrl: videoDto.baseUrl,
        quality: videoDto.outputFiles.videos.map(videoFileInfo => videoFileInfo.quality)
    }
}

export function wixMediaManagerSiteFileDtoToWixSiteFile (siteFileDto: WixMediaManager.SiteFileDto) : WixSiteFile {
    return {
        mimeType: siteFileDto.mimeType,
        fileSize: siteFileDto.fileSize,
        fileUrl: siteFileDto.fileUrl,
        metaSiteId: siteFileDto.siteId
    }
}

export const WixMediaManagerMediaType = {
    Image: 'picture',
    Video: 'video',
    VectorImage: 'shape',
    Document: 'document'
} as const;

export type WixMediaManagerMediaType = Values<typeof WixMediaManagerMediaType>;

export function wixMediaManagerItemIsDocumentItem (mediaManagerItem: WixMediaGalleryItem) : mediaManagerItem is WixMediaManagerDocumentItem {
    return mediaManagerItem.mediaType === WixMediaManagerMediaType.Document;
}
